/**
 * Unified Active Sync Status Hook
 * 
 * Consolidates WebSocket and polling logic for sync operations.
 * Replaces duplicate implementations in admin and inspector interfaces.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { SyncOperation, apiClient } from '../services/api'
import useSyncWebSocket from '../components/useSyncWebSocket'
import { debugLog, debugWarn } from '../utils/debugLog'

interface UseActiveSyncStatusOptions {
  fpCode?: string // Filter for specific franchise partner (inspector mode)
  autoRefresh?: boolean // Enable automatic refresh polling
  refreshInterval?: number // Polling interval in milliseconds
  onSyncComplete?: (sync: SyncOperation) => void // Callback when sync completes
  onSyncUpdate?: (sync: SyncOperation) => void // Callback on any sync update
}

interface UseActiveSyncStatusReturn {
  operations: SyncOperation[]
  isLoading: boolean
  error: string | null
  isConnected: boolean
  connectionStatus: string
  refetch: (force?: boolean) => Promise<void>
  setError: (error: string | null) => void
  stats: {
    total: number
    pending: number
    running: number
    totalRecordsProcessing: number
    avgProgress: number
  }
}

export const useActiveSyncStatus = (
  options: UseActiveSyncStatusOptions = {}
): UseActiveSyncStatusReturn => {
  const {
    fpCode,
    autoRefresh = true,
    refreshInterval = 3000,
    onSyncComplete,
    onSyncUpdate
  } = options

  // State
  const [operations, setOperations] = useState<SyncOperation[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shouldPoll, setShouldPoll] = useState(autoRefresh)

  // Refs for values that don't need to trigger re-renders
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastPollTimeRef = useRef(0)
  const currentIntervalRef = useRef(refreshInterval)
  const operationsRef = useRef<SyncOperation[]>([])
  const retryCountRef = useRef(0)
  const maxRetries = 3

  // Update refs when operations change
  useEffect(() => {
    operationsRef.current = operations
  }, [operations])

  // WebSocket connection for real-time updates
  const { isConnected, lastUpdate, connectionStatus } = useSyncWebSocket({
    fpCode,
    autoReconnect: true,
    reconnectInterval: 3000
  })

  // Fetch active syncs function with error recovery
  const fetchActiveSyncs = useCallback(async (force = false): Promise<SyncOperation[]> => {
    // Prevent rapid polling - ensure minimum 1 second between requests
    const now = Date.now()
    const timeSinceLastPoll = now - lastPollTimeRef.current
    if (!force && timeSinceLastPoll < 1000) {
      debugWarn(`⚠️ Skipping poll - too soon (${timeSinceLastPoll}ms since last poll)`)
      return operationsRef.current // Return existing data
    }
    lastPollTimeRef.current = now

    try {
      // Add cache-busting timestamp and retry logic
      const endpoint = fpCode 
        ? `/sync/operations/active/${fpCode}?t=${now}&retry=${retryCountRef.current}`
        : `/sync/operations/active?t=${now}&retry=${retryCountRef.current}`
      
      const response = await apiClient.get(endpoint)
      const newOperations = response.data || []
      
      // Reset retry count on success
      retryCountRef.current = 0
      
      return newOperations
    } catch (err: any) {
      // Implement exponential backoff
      retryCountRef.current++
      const backoffDelay = Math.min(1000 * Math.pow(2, retryCountRef.current), 10000) // Max 10 seconds
      
      console.error('Failed to fetch active syncs:', err)
      
      // If we haven't exceeded max retries, schedule a retry
      if (retryCountRef.current < maxRetries) {
        debugLog(`⏳ Retrying in ${backoffDelay}ms (attempt ${retryCountRef.current}/${maxRetries})`)
        setTimeout(() => {
          fetchActiveSyncs(true)
        }, backoffDelay)
      } else {
        // Reset retry count and throw error
        retryCountRef.current = 0
        throw new Error(err?.response?.data?.detail || err?.message || 'Failed to fetch active syncs')
      }
      
      return operationsRef.current // Return existing data on error
    }
  }, [fpCode])

  // Main refetch function with loading state
  const refetch = useCallback(async (force = false) => {
    if (!force && isLoading) return // Prevent concurrent requests
    
    // Update last refetch time when we actually refetch
    lastRefetchTimeRef.current = Date.now()
    
    setIsLoading(true)
    setError(null)
    
    try {
      const newOperations = await fetchActiveSyncs(force)
      
      // Check for completed syncs
      if (onSyncComplete && operationsRef.current.length > 0) {
        const currentSyncIds = new Set(newOperations.map(op => op.operation_id))
        const completedSyncs = operationsRef.current.filter(op => 
          !currentSyncIds.has(op.operation_id) && 
          (op.status === 'running' || op.status === 'pending')
        )
        
        completedSyncs.forEach(sync => {
          // Create a completed version of the sync for the callback
          onSyncComplete({ ...sync, status: 'completed' } as SyncOperation)
        })
      }
      
      setOperations(newOperations)
      
      // Update polling interval based on activity
      const hasActiveSync = newOperations.length > 0
      const newInterval = hasActiveSync ? 3000 : 30000 // 3s active, 30s idle
      
      if (newInterval !== currentIntervalRef.current) {
        currentIntervalRef.current = newInterval
        debugLog(`⏱️ Polling interval updated: ${newInterval/1000}s (active syncs: ${newOperations.length})`)
        
        // Restart polling with new interval
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        
        if (shouldPoll && autoRefresh) {
          intervalRef.current = setInterval(() => {
            refetch()
          }, newInterval)
        }
      }
      
    } catch (err: any) {
      setError(err.message)
      console.error('Error in refetch:', err)
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, fetchActiveSyncs, onSyncComplete, shouldPoll, autoRefresh])

  // Initial fetch on mount
  useEffect(() => {
    refetch(true)
  }, []) // Empty dependency to run only on mount

  // Polling setup
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    
    if (shouldPoll && autoRefresh) {
      intervalRef.current = setInterval(() => {
        refetch()
      }, currentIntervalRef.current)
      debugLog(`🔄 Polling started with interval: ${currentIntervalRef.current/1000}s`)
    } else {
      debugLog('⏸️ Polling stopped')
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [shouldPoll, autoRefresh])

  // Add a ref to track last refetch time to prevent rapid refreshes
  const lastRefetchTimeRef = useRef<number>(0)
  
  // Handle WebSocket updates
  useEffect(() => {
    if (lastUpdate) {
      debugLog('🔄 WebSocket sync update received')
      
      // Call update callback if provided
      if (onSyncUpdate) {
        onSyncUpdate(lastUpdate)
      }
      
      // Only refetch if at least 1 second has passed since last refetch
      const now = Date.now()
      if (now - lastRefetchTimeRef.current > 1000) {
        debugLog('Triggering refresh after WebSocket update')
        lastRefetchTimeRef.current = now
        refetch(true)
      } else {
        debugLog('Skipping refresh (too soon after last refetch)')
      }
      
      // If we got an active sync update, ensure we're polling frequently
      if (lastUpdate.status === 'pending' || lastUpdate.status === 'running') {
        if (currentIntervalRef.current > 3000) {
          currentIntervalRef.current = 3000
          debugLog('🎯 Active sync detected via WebSocket, setting 3s polling')
          
          // Restart polling with new interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
          }
          if (shouldPoll && autoRefresh) {
            intervalRef.current = setInterval(() => {
              refetch()
            }, 3000)
          }
        }
      }

      // Handle completion notifications
      if (lastUpdate.status === 'completed' && onSyncComplete) {
        onSyncComplete(lastUpdate)
      }
    }
  }, [lastUpdate, onSyncComplete, onSyncUpdate, shouldPoll, autoRefresh])

  // Handle fpCode changes for inspector mode
  useEffect(() => {
    if (fpCode !== undefined) {
      debugLog('📝 FP code changed, refreshing sync data')
      refetch(true)
    }
  }, [fpCode])

  // Wake on cross-component sync triggers (e.g. the drawer's "Sync
  // latest data" or the page-header RefreshDataButton call into
  // ``useSyncTrigger``, which dispatches ``evop:sync-triggered``). The
  // header bar would otherwise wait up to 30s for the next idle poll
  // before noticing the new operation.
  useEffect(() => {
    const onTriggered = (e: Event) => {
      const detail = (e as CustomEvent).detail as { fpCode?: string } | undefined
      // Filter to our FP scope when we're scoped (inspector mode); the
      // admin all-FP view (fpCode undefined) refetches on any trigger.
      if (fpCode && detail?.fpCode && detail.fpCode !== fpCode) return
      debugLog('🎯 evop:sync-triggered received, refreshing sync data')
      refetch(true)
    }
    window.addEventListener('evop:sync-triggered', onTriggered as EventListener)
    return () => {
      window.removeEventListener('evop:sync-triggered', onTriggered as EventListener)
    }
  }, [fpCode, refetch])

  // Calculate statistics
  const stats = useCallback(() => {
    const safeOperations = operations || []
    
    const pendingCount = safeOperations.filter(op => op.status === 'pending').length
    const runningCount = safeOperations.filter(op => op.status === 'running').length
    const totalRecordsProcessing = safeOperations.reduce((sum, op) => sum + (op.records_processed || 0), 0)
    const avgProgress = safeOperations.length > 0 
      ? safeOperations.reduce((sum, op) => sum + (op.progress_percentage || 0), 0) / safeOperations.length 
      : 0

    return {
      total: safeOperations.length,
      pending: pendingCount,
      running: runningCount,
      totalRecordsProcessing,
      avgProgress: Math.round(avgProgress)
    }
  }, [operations])()

  // Control polling based on activity
  useEffect(() => {
    const hasActiveOperations = operations.length > 0
    if (hasActiveOperations && !shouldPoll) {
      setShouldPoll(true)
    } else if (!hasActiveOperations && shouldPoll && !autoRefresh) {
      // Only stop polling if autoRefresh is disabled
      setShouldPoll(false)
    }
  }, [operations.length, shouldPoll, autoRefresh])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  return {
    operations,
    isLoading,
    error,
    isConnected,
    connectionStatus,
    refetch,
    setError,
    stats
  }
}

export default useActiveSyncStatus